import gql from 'graphql-tag';
import apolloClient from '../apollo';
import dayjs from 'dayjs';

import relativeTime from 'dayjs/plugin/relativeTime';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/de';

dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(dayOfYear);
dayjs.locale('de');

/**
 * State
 */
const defaultTask = () => {
  return {
    id: '_new',
    number: '_new',
    taskState: 'NEW',
    project: null,
    inCharge: null,
  };
};

const getDefaultState = () => {
  return {
    tasks: [],
    newTasks: [],
    userTasks: [],
    taskCount: 0,
    newTaskCount: 0,
    userTaskCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an task
 * used for all queries/mutations which return an task
 * @type {DocumentNode}
 */
const TASK_REQUEST_FIELDS = gql`
  fragment taskFields on Task {
    id
    number
    taskState
    description
    notes
    project {
      id
      number
      employerProjectNumber
      customer {
        firstname
        lastname
        street
        streetNumber
        zip
        city
        email
      }
    }
    inCharge {
      id
      number
      firstname
      lastname
    }
    createdAt
    updatedAt
  }
`;

/**
 * Payload of the block project-tasks (`ProjectTaskInput`): without project, the server sets it.
 * @param {*} task
 */
const createProjectTaskPayload = (task) => ({
  ...(task.taskState ? { taskState: task.taskState } : {}),
  ...(task.inCharge ? { inCharge: task.inCharge.id } : {}),
  description: task.description,
  notes: task.notes,
});

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} task
 */
const createPayload = (task) => ({
  ...createProjectTaskPayload(task),
  ...(task.project ? { project: task.project.id } : {}),
});

const TASK_LIST_QUERY = gql`
  query tasks(
    $page: Int!
    $pageSize: Int!
    $sortField: String
    $sortOrder: Int
    $filters: TaskFilterInput
  ) {
    tasks(
      page: $page
      pageSize: $pageSize
      sortField: $sortField
      sortOrder: $sortOrder
      filters: $filters
    ) {
      tasks {
        ...taskFields
      }
      taskCount
    }
  }
  ${TASK_REQUEST_FIELDS}
`;

/** Task list of the block project-tasks; the server restricts it to the project. */
const PROJECT_TASK_LIST_QUERY = gql`
  query projectTasks(
    $projectNumber: Int!
    $page: Int!
    $pageSize: Int!
    $sortField: String
    $sortOrder: Int
    $filters: TaskFilterInput
  ) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        tasks {
          id
          number
          tasks(
            page: $page
            pageSize: $pageSize
            sortField: $sortField
            sortOrder: $sortOrder
            filters: $filters
          ) {
            tasks {
              ...taskFields
            }
            taskCount
          }
        }
      }
    }
  }
  ${TASK_REQUEST_FIELDS}
`;

/**
 * Request of the task list: with a project number the block project-tasks, without it the global
 * query tasks. `pagesOf` returns the `TaskPages` of the response.
 */
const taskListRequest = (projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_TASK_LIST_QUERY,
      pagesOf: (data) => data.projectByNumber.blocks.tasks.tasks,
    };
  }
  return { query: TASK_LIST_QUERY, pagesOf: (data) => data.tasks };
};

/**
 * Request of a create: with a project number the mutation of the block project-tasks (payload
 * without project), without it the global mutation.
 */
const createTaskRequest = (task, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation createProjectTask($projectNumber: Int!, $payload: ProjectTaskInput!) {
          createProjectTask(projectNumber: $projectNumber, taskInput: $payload) {
            ...taskFields
          }
        }
        ${TASK_REQUEST_FIELDS}
      `,
      variables: { projectNumber, payload: createProjectTaskPayload(task) },
      resultField: 'createProjectTask',
    };
  }
  return {
    mutation: gql`
      mutation createTask($payload: TaskInput!) {
        createTask(taskInput: $payload) {
          ...taskFields
        }
      }
      ${TASK_REQUEST_FIELDS}
    `,
    variables: { payload: createPayload(task) },
    resultField: 'createTask',
  };
};

/** Request of an update, chosen by the project number as `createTaskRequest`. */
const updateTaskRequest = (task, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation updateProjectTask(
          $projectNumber: Int!
          $taskNumber: Int!
          $payload: ProjectTaskInput!
        ) {
          updateProjectTask(
            projectNumber: $projectNumber
            taskNumber: $taskNumber
            taskInput: $payload
          ) {
            ...taskFields
          }
        }
        ${TASK_REQUEST_FIELDS}
      `,
      variables: {
        projectNumber,
        taskNumber: task.number,
        payload: createProjectTaskPayload(task),
      },
      resultField: 'updateProjectTask',
    };
  }
  return {
    mutation: gql`
      mutation updateTask($taskNumber: Int!, $payload: TaskInput!) {
        updateTask(taskNumber: $taskNumber, taskInput: $payload) {
          ...taskFields
        }
      }
      ${TASK_REQUEST_FIELDS}
    `,
    variables: { taskNumber: task.number, payload: createPayload(task) },
    resultField: 'updateTask',
  };
};

/** Request of a delete, chosen by the project number as `createTaskRequest`. */
const deleteTaskRequest = (taskNumber, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation deleteProjectTask($projectNumber: Int!, $taskNumber: Int!) {
          deleteProjectTask(projectNumber: $projectNumber, taskNumber: $taskNumber)
        }
      `,
      variables: { projectNumber, taskNumber },
    };
  }
  return {
    mutation: gql`
      mutation deleteTask($taskNumber: Int!) {
        deleteTask(taskNumber: $taskNumber)
      }
    `,
    variables: { taskNumber },
  };
};

/**
 * Actions
 */
const actions = {
  /**
   * Loads a page of tasks. With `projectNumber` the tasks of the block project-tasks, without it
   * the global list (view tasks).
   */
  async fetchTasks(
    { commit, dispatch },
    { page, pageSize, sortField, sortOrder, filters, taskListType = 'TASKS', projectNumber }
  ) {
    const { query, pagesOf } = taskListRequest(projectNumber);
    try {
      const response = await apolloClient.query({
        query,
        variables: {
          ...(projectNumber ? { projectNumber } : {}),
          page: page,
          pageSize: pageSize,
          sortField: sortField,
          sortOrder: sortOrder,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      const taskPages = pagesOf(response.data);

      if (taskListType === 'NEW_TASKS') {
        commit('SET_NEW_TASKS', taskPages.tasks);
        commit('SET_NEW_TASK_COUNT', taskPages.taskCount);
      } else if (taskListType === 'USER_TASKS') {
        commit('SET_USER_TASKS', taskPages.tasks);
        commit('SET_USER_TASK_COUNT', taskPages.taskCount);
      } else {
        commit('SET_TASKS', taskPages.tasks);
        commit('SET_TASK_COUNT', taskPages.taskCount);
      }
    } catch (err) {
      throw new Error(err);
    }
  },

  /** Creates the task; with `projectNumber` through the block project-tasks. */
  async createTask({ commit, dispatch }, { task, projectNumber }) {
    const { mutation, variables, resultField } = createTaskRequest(task, projectNumber);

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      const createdTask = response.data[resultField];
      dispatch('message', {
        message: 'Aufgabe gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_TASK', '_new'); // remove temporary _new task
      commit('ADD_OR_UPDATE_TASK', createdTask);
      return createdTask.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Updates the task; with `projectNumber` through the block project-tasks. */
  async updateTask({ commit, dispatch }, { task, projectNumber }) {
    const { mutation, variables, resultField } = updateTaskRequest(task, projectNumber);

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Aufgabe aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_TASK', response.data[resultField]);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Deletes the task; with `projectNumber` through the block project-tasks. */
  async deleteTask({ commit, dispatch }, { taskNumber, projectNumber }) {
    try {
      const response = await apolloClient.mutate(deleteTaskRequest(taskNumber, projectNumber));
      dispatch('message', {
        message: 'Aufgabe gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      // console.log(response.data.deleteTask);

      commit('REMOVE_TASK', taskNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async sendTaskMail({ commit, dispatch }, { taskNumber }) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation sendTaskMail($taskNumber: Int!) {
            sendTaskMail(taskNumber: $taskNumber) {
              ...taskFields
            }
          }
          ${TASK_REQUEST_FIELDS}
        `,
        variables: {
          taskNumber: taskNumber,
        },
      });
      dispatch('message', {
        message: 'Nachricht verschickt!',
        type: 'success',
        lifetime: 1,
      });

      commit('ADD_OR_UPDATE_TASK', response.data.sendTaskMail);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Versand!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new task object in store and return it
   * @param {*} param0
   * @param {*} task
   */
  async initTask({ commit, state }) {
    const newTask = defaultTask();
    commit('ADD_OR_UPDATE_TASK', newTask);
  },

  /**
   * create a new task with existing data
   * @param {*} param0
   * @param {*} task
   */
  async duplicateTask({ commit }, { task, installationStartAt }) {
    const duplicate = Object.assign(defaultTask(), {
      taskType: task.taskType,
      project: task.project,
      employees: task.employees,
      description: task.description,
      installationStartAt: installationStartAt,
      installationEndAt: dayjs(installationStartAt)
        .add(8, 'hour')
        .toDate(),
    });

    // console.log(duplicate);
    commit('ADD_OR_UPDATE_TASK', duplicate);
    return duplicate;
  },

  taskLocalUpdate({ commit }, task) {
    commit('ADD_OR_UPDATE_TASK', task);
  },

  taskCleanup({ commit }) {
    commit('REMOVE_TASK', '_new');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_TASK_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_TASKS: (state, tasks) => (tasks ? (state.tasks = tasks) : (state.tasks = [])),
  SET_NEW_TASKS: (state, tasks) => (state.newTasks = tasks || []),
  SET_USER_TASKS: (state, tasks) => (state.userTasks = tasks || []),
  ADD_TASK: (state, task) => state.tasks.push(task),
  ADD_OR_UPDATE_TASK: (state, updatedTask) => {
    const existingTask = state.tasks.find((task) => task.id === updatedTask.id);
    if (existingTask) {
      Object.assign(existingTask, updatedTask);
    } else {
      state.tasks.unshift(updatedTask);
    }
  },
  REMOVE_TASK: (state, taskNumber) => {
    // console.log(taskNumber);
    const index = state.tasks.findIndex((task) => task.number === taskNumber);
    state.tasks.splice(index, 1);
  },
  SET_TASK_COUNT: (state, taskCount) => (state.taskCount = taskCount),
  SET_NEW_TASK_COUNT: (state, taskCount) => (state.newTaskCount = taskCount),
  SET_USER_TASK_COUNT: (state, taskCount) => (state.userTaskCount = taskCount),
};

/**
 * Getters
 */
const getters = {
  getTasks: (state) => state.tasks,
  getNewTasks: (state) => state.newTasks,
  getUserTasks: (state) => state.userTasks,
  getTask: (state) => (taskId) => state.tasks.find((task) => task.id == taskId),
  getTaskByNumber: (state) => (taskNumber) => state.tasks.find((task) => task.number == taskNumber),
  getTaskCount: (state) => state.taskCount,
  getNewTaskCount: (state) => state.newTaskCount,
  getUserTaskCount: (state) => state.userTaskCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
